import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';

import { Button } from '@/components/Button';
import { PasswordInput } from '@/components/PasswordInput';
import { updatePassword, signOut, AuthActionError } from '@/lib/auth-actions';
import { resetConfirmSchema, type ResetConfirmValues } from '@/lib/auth-schemas';
import { getSupabaseClient } from '@/lib/supabase';

export function ResetPasswordConfirmPage() {
  const { t } = useTranslation(['auth']);
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetConfirmValues>({
    resolver: zodResolver(resetConfirmSchema(t)),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      await updatePassword(values.newPassword);
      // Admins (e.g. invited via the recovery flow) re-authenticate on the
      // back-office login; buyers on the public sign-in. Resolve the role from
      // the recovery session before signing out.
      let target = '/signin';
      try {
        const supabase = getSupabaseClient();
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('users')
            .select('role')
            .eq('id', user.id)
            .single();
          if (profile?.role === 'admin' || profile?.role === 'super_admin') {
            target = '/admin/login';
          }
        }
      } catch {
        // Fall back to the buyer sign-in if the role lookup fails.
      }
      // R13 — recovery flow signs the user out so they re-auth with the new
      // password (avoids stale sessions on shared devices).
      await signOut().catch(() => undefined);
      toast.success(t('auth:reset.confirmSuccess'));
      navigate(target, { replace: true });
    } catch (error) {
      if (error instanceof AuthActionError) {
        if (error.code === 'token_invalid') {
          toast.error(t('auth:errors.tokenInvalid'));
        } else if (error.code === 'weak_password') {
          toast.error(t('auth:errors.weakPassword'));
        } else if (error.code === 'rate_limited') {
          toast.error(t('auth:errors.rateLimited'));
        } else {
          toast.error(t('auth:errors.generic'));
        }
      } else {
        toast.error(t('auth:errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <section className="m5-shell">
      <h1 className="m5-shell__title">{t('auth:reset.confirmTitle')}</h1>
      <p className="m5-shell__lead">{t('auth:reset.confirmLead')}</p>

      <div className="m5-card">
        <form className="m5-form" onSubmit={onSubmit} noValidate>
          <PasswordInput
            label={t('auth:fields.newPassword')}
            autoComplete="new-password"
            required
            helper={t('auth:signIn.passwordHelper')}
            error={errors.newPassword?.message}
            showLabel={t('auth:common.passwordShow')}
            hideLabel={t('auth:common.passwordHide')}
            {...register('newPassword')}
          />
          <PasswordInput
            label={t('auth:fields.confirmPassword')}
            autoComplete="new-password"
            required
            error={errors.confirmPassword?.message}
            showLabel={t('auth:common.passwordShow')}
            hideLabel={t('auth:common.passwordHide')}
            {...register('confirmPassword')}
          />
          <Button
            type="submit"
            variant="primary"
            loading={submitting}
            loadingLabel={t('auth:common.loading')}
          >
            {t('auth:reset.confirmSubmit')}
          </Button>
        </form>

        <p className="m5-footnote">
          <Link to="/signin" className="m5-link">
            {t('auth:reset.backToSignIn')}
          </Link>
        </p>
      </div>
    </section>
  );
}
